import { useState, useEffect } from "react"
import {
  FormData,
  TreeEntry,
  FarmerPayload,
  FormErrors,
} from "../types"
import { TreeType } from "../../../types"
import { farmerService, treeTypeService, regionService } from "../../../services"

const EMPTY_FORM: FormData = {
  name: "",
  phone: "",
  region: "",
  farmSize: "",
  arableSize: "",
  treeCoverage: "",
  latitude: "",
  longitude: "",
  trees: [],
}

const buildPayload = (form: FormData, treeTypes: TreeType[]): FarmerPayload => ({
  name:                form.name.trim(),
  phone:               form.phone.trim(),
  region_id:           Number(form.region),
  farm_size_range:     form.farmSize     || null,
  arable_size_range:   form.arableSize   || null,
  tree_coverage_range: form.treeCoverage || null,
  latitude:            parseFloat(form.latitude),
  longitude:           parseFloat(form.longitude),
  trees: form.trees
    .filter(t => t.count && Number(t.count) > 0)
    .map(t => {
      const match = treeTypes.find(
        tt => tt.name.toLowerCase() === t.name.toLowerCase()
      )
      return {
        tree_type_id: match?.id ?? t.tree_type_id,
        count:        Number(t.count),
        age_years:    t.age_years ? Number(t.age_years) : null,
        condition:    t.condition,
      }
    })
    .filter(t => t.tree_type_id > 0),
})

export const useRegisterForm = () => {
  const [form, setForm]           = useState<FormData>(EMPTY_FORM)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading]     = useState(false)
  const [gpsLoading, setGpsLoading] = useState(false)
  const [treeInput, setTreeInput] = useState("")
  const [errors, setErrors]       = useState<FormErrors>({})
  const [regions, setRegions]     = useState<Array<{id: number, name: string}>>([])
  const [treeTypes, setTreeTypes] = useState<Array<{id: number, name: string}>>([])

  const setField = (key: keyof FormData, value: string) => {
    setForm(f => ({ ...f, [key]: value }))
    if (errors[key as keyof FormErrors]) {
      setErrors(e => ({ ...e, [key]: undefined }))
    }
  }

  const addTree = (name: string) => {
    const trimmed = name.trim()
    if (!trimmed) return
    if (form.trees.find(t => t.name.toLowerCase() === trimmed.toLowerCase())) return
    const newTree: TreeEntry = {
      id:           Date.now().toString(),
      name:         trimmed,
      count:        "",
      condition:    "good",
      tree_type_id: 0,
    }
    setForm(f => ({ ...f, trees: [...f.trees, newTree] }))
    setTreeInput("")
    setErrors(e => ({ ...e, trees: undefined }))
  }

  const removeTree = (id: string) =>
    setForm(f => ({ ...f, trees: f.trees.filter(t => t.id !== id) }))

  const updateTree = (id: string, key: keyof TreeEntry, value: string) =>
    setForm(f => ({
      ...f,
      trees: f.trees.map(t => t.id === id ? { ...t, [key]: value } : t),
    }))

  const validate = (): boolean => {
    const e: FormErrors = {}

    if (!form.name.trim())
      e.name = "Farmer name is required."

    if (!form.phone.trim())
      e.phone = "Phone number is required."
    else if (!/^[+\d\s-]{7,20}$/.test(form.phone.trim()))
      e.phone = "Enter a valid phone number."

    if (!form.region)
      e.region = "Please select a region."

    if (!form.latitude || !form.longitude)
      e.latitude = "Farm GPS location is required. Use GPS or pick on map."

    setErrors(e)
    return Object.keys(e).length === 0
  }

  const captureGPS = () => {
    setGpsLoading(true)
    navigator.geolocation?.getCurrentPosition(
      pos => {
        setField("latitude",  pos.coords.latitude.toFixed(6))
        setField("longitude", pos.coords.longitude.toFixed(6))
        setGpsLoading(false)
      },
      () => {
        setField("latitude",  "-1.286389")
        setField("longitude", "36.817223")
        setGpsLoading(false)
      },
      { enableHighAccuracy: true, timeout: 8000 }
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setLoading(true)
    setErrors({})

    try {
      const payload: FarmerPayload  = buildPayload(form, treeTypes as TreeType[])
      await farmerService.registerWithPayload(payload)
      setSubmitted(true)
    } catch (error: any) {
      const serverErrors = error.response?.data?.errors
      if (serverErrors) {
        setErrors({
          name:     serverErrors.name?.[0],
          phone:    serverErrors.phone?.[0],
          region:   serverErrors.region_id?.[0],
          latitude: serverErrors.latitude?.[0] ?? serverErrors.longitude?.[0],
        })
      } else {
        setErrors({
          general: error.response?.data?.message
            ?? "Something went wrong. Please try again.",
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchRegions = async () => {
    try {
      const regionsData = await regionService.getAll()
      setRegions(regionsData)
    } catch (error) {
      console.error("Failed to fetch regions:", error)
    }
  }

  const fetchTreeTypes = async () => {
    try {
      const treeTypesData = await treeTypeService.getAll()
      setTreeTypes(treeTypesData)
    } catch (error) {
      console.error("Failed to fetch tree types:", error)
    }
  }

  useEffect(() => {
    fetchRegions()
    fetchTreeTypes()
  }, [])

  const resetForm = () => {
    setSubmitted(false)
    setForm(EMPTY_FORM)
    setErrors({})
    setTreeInput("")
  }

  return {
    form, submitted, loading, gpsLoading,
    treeInput, errors, regions, treeTypes,
    setTreeInput, setField,
    addTree, removeTree, updateTree,
    captureGPS, handleSubmit, resetForm,
  }
}